import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { displayPatientName } from "../../utils/CommonUtil";
import "../../utils/FontAwesome";
import {
  Table,
  Alert,
  Checkbox,
  Group,
  Loader,
  Stack,
  Card,
  Text,
  SimpleGrid,
  Box,
  Button,
  Pagination,
  Anchor,
  Flex,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { TestListProps, TestListItem } from "./types/testListTypes";
import { groupTestsByDate } from "./utils/testListUtils";

export default function TestList(props: TestListProps) {
  const {
    data: testList = [],
    loading = false,
    error = null,
    totalPages = 1,
    currentPage = 1,
    onPageChange,
    onRowClick,
    onItemClick,
    columns: customColumns,
    showDateGroups = true,
    showActionButtons = false,
    onEdit,
    onDelete,
    viewMode: propViewMode,
    onViewModeChange,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [selectAll, setSelectAll] = useState(true);

  const [internalViewMode, setInternalViewMode] = useState<"table" | "card">(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768 ? "card" : "table";
    }
    return "table";
  });

  const viewMode = propViewMode ?? internalViewMode;

  useEffect(() => {
    if (isMobile && !propViewMode) {
      setInternalViewMode("card");
    }
  }, [isMobile, propViewMode]);

  const columns = customColumns || [];

  const handleRowClick = (row: TestListItem, index: number) => {
    if (onItemClick) {
      onItemClick(row, index);
    }
    if (onRowClick) {
      onRowClick(row, index);
    }
    if (!onItemClick && !onRowClick) {
      window.open(window.location.origin + "/test-result?uuid=" + row.uuid, "_blank");
    }
  };

  const cardView = () => {
    const groupedTests = groupTestsByDate(testList);

    const generateCard = (test: TestListItem, index: number) => {
      return (
        <Card
          key={test.uuid || index}
          shadow="xs"
          padding="lg"
          radius="lg"
          withBorder
          h="100%"
          style={{
            cursor: "pointer",
            transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
            borderColor: "var(--oculera-border-subtle)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "var(--mantine-shadow-md)";
            e.currentTarget.style.borderColor = "var(--mantine-color-blue-4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "var(--mantine-shadow-xs)";
            e.currentTarget.style.borderColor = "var(--oculera-border)";
          }}
          onClick={() => handleRowClick(test, index)}
        >
          <Flex direction="column" gap="xs" h="100%">
            <Group justify="space-between" align="flex-start">
              <Box flex={1}>
                {restProps.withCheckBox && (
                  <Checkbox
                    checked={!test.unSelect}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_o) => {
                      test.unSelect = !test.unSelect;
                      if (restProps.updateList) {
                        restProps.updateList(testList, false);
                      }
                    }}
                    mb="xs"
                  />
                )}
                <Text fw={600} size="lg" mb="xs" c="inherit" lh={1.4}>
                  <Anchor
                    href={"test-result?uuid=" + test.uuid}
                    c="blue.6"
                    underline="never"
                    style={{ transition: "color 0.2s ease" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--mantine-color-blue-7)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--mantine-color-blue-6)";
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRowClick(test, index);
                    }}
                  >
                    {columns[0]?.formatter
                      ? columns[0].formatter(test[columns[0].dataField as keyof TestListItem], test)
                      : displayPatientName(test.patientName)}
                  </Anchor>
                </Text>
              </Box>
            </Group>
            <Flex direction="column" gap="xs" mt="xs" style={{ flex: 1 }}>
              {columns.slice(1).map((col, colIndex) => {
                const cellValue = col.formatter
                  ? col.formatter(test[col.dataField as keyof TestListItem], test)
                  : test[col.dataField as keyof TestListItem];

                return (
                  <Group
                    key={colIndex}
                    justify="space-between"
                    gap="xs"
                    py={4}
                    style={{ borderBottom: "1px solid var(--oculera-border-subtle)" }}
                  >
                    <Text size="sm" c="dimmed" fw={500} miw={120}>
                      {col.text}:
                    </Text>
                    <Text size="sm" fw={400} ta="right" w="100%">
                      {cellValue || "-"}
                    </Text>
                  </Group>
                );
              })}
            </Flex>
            {showActionButtons && (
              <Group
                gap="xs"
                justify="flex-end"
                mt="auto"
                pt="md"
                style={{ borderTop: "1px solid var(--oculera-border-subtle)" }}
              >
                {onEdit && (
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(test, index);
                    }}
                  >
                    {t("edit") || "Edit"}
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(test, index);
                    }}
                  >
                    {t("delete") || "Delete"}
                  </Button>
                )}
              </Group>
            )}
          </Flex>
        </Card>
      );
    };

    return (
      <Box mah="100%" pb="2rem" style={{ overflowY: "auto" }}>
        {restProps.evaluateList && restProps.evaluateList(testList) ? (
          <p>{restProps.evaluateList(testList)}</p>
        ) : null}
        {showDateGroups ? (
          <Stack gap="md">
            {Object.entries(groupedTests)
              .filter((dateListObj) => dateListObj && dateListObj[1].length > 0)
              .map((dateListObj, _groupIndex) => {
                return (
                  <Box key={dateListObj[0]} mb="xl">
                    <Text
                      fw={700}
                      size="xl"
                      mb="lg"
                      tt="uppercase"
                      pb="sm"
                      style={{
                        letterSpacing: "0.5px",
                        borderBottom: "2px solid var(--mantine-color-blue-4)",
                      }}
                    >
                      {t("NewTest." + dateListObj[0])}
                    </Text>
                    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
                      {dateListObj[1].map((test, index) => generateCard(test, index))}
                    </SimpleGrid>
                  </Box>
                );
              })}
          </Stack>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" mt="sm">
            {testList.map((test, index) => generateCard(test, index))}
          </SimpleGrid>
        )}
      </Box>
    );
  };

  const responsiveTable = () => {
    const groupedTests = groupTestsByDate(testList);

    const generateTableRows = (list: TestListItem[]) => {
      return (
        <>
          {list.map((test, index) => (
            <Table.Tr
              key={test.uuid || index}
              onClick={() => handleRowClick(test, index)}
              style={{
                cursor: "pointer",
                transition: "background-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--mantine-color-blue-light)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "";
              }}
            >
              {columns.map((col, colIndex) => {
                if (colIndex === 0) {
                  return (
                    <Table.Td
                      key={colIndex}
                      px="lg"
                      py="md"
                      fz="sm"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 200,
                        borderBottom: "1px solid var(--oculera-border-subtle)",
                      }}
                    >
                      <Group wrap="nowrap" gap="sm" pl="xs">
                        {restProps.withCheckBox ? (
                          <Checkbox
                            checked={!test.unSelect}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(_o) => {
                              test.unSelect = !test.unSelect;
                              if (restProps.updateList) {
                                restProps.updateList(testList, false);
                              }
                            }}
                          />
                        ) : null}
                        <Anchor
                          href={"test-result?uuid=" + test.uuid}
                          c="blue.6"
                          underline="never"
                          size="sm"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            fontWeight: 500,
                            textDecoration: "none",
                            transition: "color 0.2s ease, text-decoration 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "var(--mantine-color-blue-7)";
                            e.currentTarget.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "var(--mantine-color-blue-6)";
                            e.currentTarget.style.textDecoration = "none";
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRowClick(test, index);
                          }}
                        >
                          {col.formatter
                            ? col.formatter(test[col.dataField as keyof TestListItem], test)
                            : displayPatientName(test.patientName)}
                        </Anchor>
                      </Group>
                    </Table.Td>
                  );
                }

                const cellValue = col.formatter
                  ? col.formatter(test[col.dataField as keyof TestListItem], test)
                  : test[col.dataField as keyof TestListItem];

                return (
                  <Table.Td
                    key={colIndex}
                    px="lg"
                    py="md"
                    fz="sm"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                      borderBottom: "1px solid var(--oculera-border-subtle)",
                    }}
                  >
                    {cellValue}
                  </Table.Td>
                );
              })}
              {showActionButtons && (
                <Table.Td
                  px="lg"
                  py="md"
                  ta="right"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 200,
                    borderBottom: "1px solid var(--oculera-border-subtle)",
                  }}
                >
                  <Group gap="xs" wrap="nowrap" justify="flex-end">
                    {onEdit && (
                      <Button
                        size="xs"
                        variant="light"
                        color="blue"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(test, index);
                        }}
                      >
                        {t("edit") || "Edit"}
                      </Button>
                    )}
                    {onDelete && (
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(test, index);
                        }}
                      >
                        {t("delete") || "Delete"}
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </>
      );
    };

    if (restProps.isTestNotFound) {
      return renderNotFound();
    } else {
      return (
        <>
          {restProps.evaluateList && restProps.evaluateList(testList) ? (
            <p>{restProps.evaluateList(testList)}</p>
          ) : null}
          <Box
            style={{
              overflow: "hidden",
              border: "1px solid var(--oculera-border)",
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Table.ScrollContainer minWidth="100%">
              <Table
                highlightOnHover
                stickyHeader
                fz={14}
                style={{
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <Table.Thead>
                  <Table.Tr
                    style={{
                      borderBottom: "2px solid var(--oculera-border)",
                      backgroundColor: "var(--oculera-surface-alt)",
                    }}
                  >
                    {columns.map((data, index) => (
                      <Table.Th
                        key={index}
                        px="lg"
                        py="md"
                        c="dimmed"
                        fw={600}
                        fz={13}
                        tt="uppercase"
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 200,
                          letterSpacing: "0.5px",
                          backgroundColor: "var(--oculera-surface-alt)",
                          borderBottom: "2px solid var(--oculera-border)",
                        }}
                      >
                        {data.text}
                      </Table.Th>
                    ))}
                    {showActionButtons && (
                      <Table.Th
                        px="lg"
                        py="md"
                        c="dimmed"
                        fw={600}
                        fz={13}
                        tt="uppercase"
                        ta="right"
                        style={{
                          letterSpacing: "0.5px",
                          backgroundColor: "var(--oculera-surface-alt)",
                          borderBottom: "2px solid var(--oculera-border)",
                        }}
                      >
                        Actions
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {showDateGroups
                    ? Object.entries(groupedTests)
                        .filter((dateListObj) => dateListObj && dateListObj[1].length > 0)
                        .map((dateListObj, index) => {
                          return (
                            <React.Fragment key={dateListObj[0]}>
                              <Table.Tr
                                style={{
                                  borderTop: "2px solid var(--oculera-border)",
                                  borderBottom: "2px solid var(--oculera-border)",
                                  backgroundColor: "var(--oculera-surface-alt)",
                                }}
                              >
                                <Table.Td
                                  colSpan={columns.length + (showActionButtons ? 1 : 0)}
                                  px="lg"
                                  py="md"
                                  fw={700}
                                  fz={14}
                                  c="dimmed"
                                  tt="uppercase"
                                  style={{
                                    letterSpacing: "0.5px",
                                    backgroundColor: "var(--oculera-surface-alt)",
                                  }}
                                >
                                  <Group gap="sm" pl="xs" justify="flex-start">
                                    {index === 0 && restProps.withCheckBox == true ? (
                                      <Checkbox
                                        defaultChecked={true}
                                        onChange={() => {
                                          setSelectAll(!selectAll);
                                        }}
                                      />
                                    ) : null}
                                    {t("NewTest." + dateListObj[0])}
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                              {generateTableRows(dateListObj[1])}
                            </React.Fragment>
                          );
                        })
                    : generateTableRows(testList)}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Box>
        </>
      );
    }
  };

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const paginate = () => {
    if (!onPageChange || totalPages <= 1) {
      return null;
    }

    return (
      <Group justify="center" mt="lg">
        <Pagination
          total={totalPages || 1}
          value={currentPage}
          onChange={handlePageChange}
          siblings={isMobile ? 1 : 2}
          boundaries={isMobile ? 1 : 2}
          withEdges
        />
      </Group>
    );
  };

  const renderNotFound = () => {
    return <Alert color={"red"}>{t("TestController.noTests")}</Alert>;
  };

  if (loading)
    return (
      <Stack align="center" justify="center" h="100%" mt="100">
        <Loader size="md" />
      </Stack>
    );
  if (error) return <Alert color="red">{error}</Alert>;

  return (
    <>
      <div>
        {testList.length > 0
          ? viewMode === "table"
            ? responsiveTable()
            : cardView()
          : renderNotFound()}
        {paginate()}
      </div>
    </>
  );
}
